/**
 * Calculate the number of nights between two dates.
 *
 * @param {string | Date} startDate - The date the guest checks into the hotel.
 * @param {string | Date} endDate - The date the guest checks out of the hotel.
 *
 * @returns {number} The total number of nights stayed.
 *
 * @example
 * DateDuration("2025-08-01", "2025-08-05"); // 4
 */
export const dateDuration = (startDate: string, endDate: string): number => {
  const diffInTime: number = new Date(endDate).getTime() - new Date(startDate).getTime();
  const duration = diffInTime / (1000 * 60 * 60 * 24);
  return Math.max(1, Math.ceil(duration));
  // return Math.ceil(duration);
};


/**
 * Returns the start and end Date objects representing the full range of the current day.
 * 
 * Useful for filtering records (e.g., bookings, bills, logs) that occurred within today.
 *
 * @returns {{ start: Date, end: Date }} An object containing:
 * - `start`: The beginning of the day (00:00:00.000)
 * - `end`: The end of the day (23:59:59.999)
 */
export function getTodayRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return { start, end };
}


/**
 * Returns the start and end Date objects for a given date string.
 *
 * @param {string} dateStr - The date in "YYYY-MM-DD" format
 * @returns {{ start: Date, end: Date }} The full range of that day
 */
export function getRangeForDate(dateStr: string) {
  const [year, month, day] = dateStr.split("-").map(Number);
  const start = new Date(year, month - 1, day, 0, 0, 0, 0);
  const end = new Date(year, month - 1, day, 23, 59, 59, 999);
  return { start, end };
}


/**
 * Returns a new Date object set to midnight (00:00:00) for comparison
 */
export function normalizeDate(date: Date | string): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}


export const todayDate = (): string => {
  return new Date().toISOString();
}


/**
 * formatDateTime
 * -----------------
 * Converts a date string or Date object into a human-readable format
 * including both the date and time in 12-hour format.
 *
 * @param dateString - The input date, either as a string or a Date object.
 * @returns A formatted string in the format: "Sep 24, 2025, 2:30 PM"
 *
 * @example
 * formatDateTime("2025-09-24T14:30:00Z");
 * // Returns: "Sep 24, 2025, 2:30 PM"
 *
 * formatDateTime(new Date());
 * // Returns current date and time in the same format
 */
export const formatDateTime = (dateString: string | Date): string => {
  const date = new Date(dateString);
  return date.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};


export const formatDate = (dateString: string | Date): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};


export const formatTime = (dateString: string | Date): string => {
  return new Date(dateString).toLocaleString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  })
}


// Convert Date object to yyyy-MM-dd
export function formatDateForInput(date: Date | string) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}


/**
 * Converts a date into a human-readable relative time string.
 *
 * @param date - A JavaScript `Date` object or an ISO date string
 *               (e.g. Prisma's `createdAt` field).
 *
 * @returns A relative time string such as:
 * - "10 secs ago"
 * - "5 mins ago"
 * - "2 hrs ago"
 * - "Yesterday"
 * - "4 days ago"
 * - "3 weeks ago"
 * - "8 months ago"
 * - "2 years ago"
 */
export function timeAgo(date: Date | string): string {
  const now = new Date();
  const createdAt = new Date(date);

  const seconds = Math.floor((now.getTime() - createdAt.getTime()) / 1000);

  if (seconds < 60) {
    return `${seconds} sec${seconds !== 1 ? "s" : ""} ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min${minutes !== 1 ? "s" : ""} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hr${hours !== 1 ? "s" : ""} ago`;
  }

  const days = Math.floor(hours / 24);

  if (days === 1) {
    return "Yesterday";
  }

  if (days < 7) {
    return `${days} day${days !== 1 ? "s" : ""} ago`;
  }

  const weeks = Math.floor(days / 7);
  if (weeks < 5) {
    return `${weeks} week${weeks !== 1 ? "s" : ""} ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months} month${months !== 1 ? "s" : ""} ago`;
  }

  const years = Math.floor(days / 365);
  return `${years} year${years !== 1 ? "s" : ""} ago`;
}


export const formatDuration = (
  start: string | Date,
  end: string | Date
) => {
  const diff = new Date(end).getTime() - new Date(start).getTime();

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return `${hours}h ${minutes}m ${seconds}s`;
};


export function getDayName(date: Date): string {
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  return days[(date.getDay() + 6) % 7];
}




export type DateRangeType = "today" | "week" | "yesterday" | "month" | "year";
export function getDateRange(type: DateRangeType) {
  const now = new Date();

  switch (type) {
    case "today": {
      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0, 0, 0, 0
      );

      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23, 59, 59, 999
      );

      return { start, end };
    }

    case "week": {
      // Monday → Sunday
      const day = now.getDay();
      const daysFromMonday = day === 0 ? 6 : day - 1;

      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - daysFromMonday,
        0, 0, 0, 0
      );

      const end = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate() + 6,
        23, 59, 59, 999
      );

      return { start, end };
    }

    case "yesterday": {
      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1,
        0, 0, 0, 0
      );

      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1,
        23, 59, 59, 999
      );

      return { start, end };
    }
    case "month": {
      // 1st → last day of current month
      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        1,
        0, 0, 0, 0
      );

      const end = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0,
        23, 59, 59, 999
      );

      return { start, end };
    }

    case "year": {
      // January 1st → December 31st
      const start = new Date(
        now.getFullYear(),
        0,
        1,
        0, 0, 0, 0
      );

      const end = new Date(
        now.getFullYear(),
        11,
        31,
        23, 59, 59, 999
      );

      return { start, end };
    }
    default: {
      return { start: null, end: null }
    }
  }
}